const STORAGE_KEY = 'platformReturnUrl';

type Origin = 'brandlive' | 'brandtv' | 'webinarplus';

const KNOWN_ORIGINS: readonly Origin[] = ['brandlive', 'brandtv', 'webinarplus'];

const PROJECT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

interface ReturnContext {
	origin: Origin;
	projectId: string;
}

function isKnownOrigin(value: string): value is Origin {
	return (KNOWN_ORIGINS as readonly string[]).includes(value);
}

function isValidProjectId(value: string): boolean {
	return PROJECT_ID_PATTERN.test(value);
}

function buildPlatformUrl(origin: Origin, projectId: string): string {
	const env = process.env.REACT_APP_NODE_ENV ?? 'dev';
	const subdomain = env === 'prod' ? origin : `${origin}-${env}`;
	return `https://create.${subdomain}.com/project/edit/${encodeURIComponent(projectId)}`;
}

function readContext(): ReturnContext | null {
	const raw = sessionStorage.getItem(STORAGE_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (
			parsed
			&& typeof parsed.origin === 'string'
			&& typeof parsed.projectId === 'string'
			&& isKnownOrigin(parsed.origin)
			&& isValidProjectId(parsed.projectId)
		) {
			return { origin: parsed.origin, projectId: parsed.projectId };
		}
	} catch {
		// fall through to null
	}
	return null;
}

export function capturePlatformReturn(): void {
	const params = new URLSearchParams(window.location.search);
	const origin = params.get('origin');
	const projectId = params.get('projectId');

	if (origin && projectId && isKnownOrigin(origin) && isValidProjectId(projectId)) {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ origin, projectId }));
	}

	if (params.has('origin') || params.has('projectId')) {
		params.delete('origin');
		params.delete('projectId');
		const newSearch = params.toString();
		const newUrl = window.location.pathname
			+ (newSearch ? `?${newSearch}` : '')
			+ window.location.hash;
		window.history.replaceState(null, '', newUrl);
	}
}

export function getReturnUrl(): string | null {
	const context = readContext();
	if (!context) return null;
	return buildPlatformUrl(context.origin, context.projectId);
}

export function clearReturnUrl(): void {
	sessionStorage.removeItem(STORAGE_KEY);
}
